import { useEffect, useRef, useState } from "react";

/**
 * IntersectionObserver-backed reveal hook. Fires once per element, then disconnects.
 * Honors prefers-reduced-motion (returns visible immediately).
 *
 * Usage:
 *   const [ref, visible] = useReveal();
 *   <div ref={ref} className={`lm-reveal ${visible ? "is-visible" : ""}`}>…</div>
 */
export function useReveal(threshold = 0.15, rootMargin = "0px 0px -8% 0px") {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Respect reduced motion
    if (typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setVisible(true);
      return;
    }
    if (!ref.current) return;
    const el = ref.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold, rootMargin },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, rootMargin]);

  return [ref, visible];
}

/** Convenience wrapper that returns a className to slap onto the element. */
export function useRevealClass(base = "lm-reveal", threshold = 0.15) {
  const [ref, visible] = useReveal(threshold);
  return [ref, `${base} ${visible ? "is-visible" : ""}`];
}
